(() => {
  if (window.brioHomeInitialized) return;
  window.brioHomeInitialized = true;
  const setupPriorityMedia = (scope = document) => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    scope.querySelectorAll('img[fetchpriority="high"]:not([data-pdp-main-image])').forEach((image) => {
      if (!image.closest('main') || image.closest('.brio-hero') || image.dataset.brioMediaReady) return;
      image.dataset.brioMediaReady = 'true';
      if (image.complete) return;
      image.classList.add('brio-media-reveal', 'brio-media-pending');
      const finish = () => {
        image.classList.remove('brio-media-pending');
        setTimeout(() => image.classList.remove('brio-media-reveal'), 550);
      };
      image.addEventListener('load', () => {
        if (image.decode) image.decode().catch(() => {}).finally(finish);
        else finish();
      }, { once: true });
      image.addEventListener('error', finish, { once: true });
    });
  };
  setupPriorityMedia();
  document.addEventListener('shopify:section:load', (event) => setupPriorityMedia(event.target));
  const setupStickyHeader = () => {
    const header = document.querySelector('.brio-banner-header');
    if (!header || header.dataset.stickyReady) return;
    header.dataset.stickyReady = 'true';
    const updateAppearance = () => {
      const flavours = document.getElementById('saveurs');
      const bounds = flavours?.getBoundingClientRect();
      const onFlavours = !!bounds && bounds.top <= header.offsetHeight + 16 && bounds.bottom > header.offsetHeight + 4;
      header.classList.toggle('brio-header-on-flavours', onFlavours);
      header.classList.toggle('brio-header-over-flavours', onFlavours && bounds.top <= 4 && bounds.bottom > header.offsetHeight);
      const surface = document.elementFromPoint(window.innerWidth / 2, Math.min(header.offsetHeight + 2, window.innerHeight - 1));
      header.classList.toggle('brio-header-on-light', !!surface?.closest('#pourquoi-brio, #brio-au-quotidien, #ou-trouver-brio, #univers-brio, #more-brio'));
      const flavoursLink = header.querySelector('.brio-desktop-nav a[href$="#saveurs"]');
      if (onFlavours) flavoursLink?.setAttribute('aria-current', 'location');
      else flavoursLink?.removeAttribute('aria-current');
    };
    const updateHeight = () => {
      if (matchMedia('(max-width: 600px)').matches) {
        document.documentElement.style.removeProperty('--brio-header-height');
        return;
      }
      document.documentElement.style.setProperty('--brio-header-height', `${header.offsetHeight}px`);
    };
    updateAppearance();
    updateHeight();
    window.addEventListener('scroll', updateAppearance, { passive: true });
    let observer;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(updateHeight);
      observer.observe(header);
    } else {
      window.addEventListener('resize', updateHeight, { passive: true });
    }
    const unload = (event) => {
      if (!event.target.contains(header)) return;
      observer?.disconnect();
      window.removeEventListener('scroll', updateAppearance);
      window.removeEventListener('resize', updateHeight);
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
  };
  setupStickyHeader();
  document.addEventListener('shopify:section:load', setupStickyHeader);
  const setupNatureMotion = () => {
    document.querySelectorAll('.brio-nature').forEach((section) => {
      if (section.dataset.natureMotionReady) return;
      section.dataset.natureMotionReady = 'true';
      section.closest('.shopify-section')?.classList.add('brio-nature-chapter');
      const hero = section.querySelector('.brio-nature-hero');
      if (!hero) return;
      if (!window.gsap || !window.ScrollTrigger || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      const landscape = hero.querySelector('.brio-nature-hero-media img');
      const eyebrow = hero.querySelector('[data-nature-eyebrow]');
      const titleLines = [...hero.querySelectorAll('.brio-nature-title-clip > *')];
      const description = hero.querySelector('[data-nature-description]');
      const benefits = [...hero.querySelectorAll('[data-nature-benefit]')];
      const sack = hero.querySelector('.brio-nature-sack-layer img');
      const cta = hero.querySelector('[data-nature-cta]');
      const reveal = gsap.timeline({ paused: true, defaults: { ease: 'power3.out' } });
      if (landscape) reveal.fromTo(landscape, { scale: 1.03 }, { scale: 1, duration: 1.15 }, 0);
      if (eyebrow) reveal.fromTo(eyebrow, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: .42 }, .08);
      if (titleLines.length) reveal.fromTo(titleLines, { autoAlpha: 0, yPercent: 110 }, { autoAlpha: 1, yPercent: 0, duration: .65, stagger: .09 }, .18);
      if (description) reveal.fromTo(description, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: .48 }, .39);
      if (benefits.length) reveal.fromTo(benefits, { autoAlpha: 0, y: 13 }, { autoAlpha: 1, y: 0, duration: .48, stagger: .09 }, .48);
      if (sack) reveal.fromTo(sack, { autoAlpha: 0, x: 20 }, { autoAlpha: 1, x: 0, duration: .75 }, .32);
      if (cta) reveal.fromTo(cta, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: .42 }, .79);
      ScrollTrigger.create({ trigger: hero, start: 'top 78%', once: true, onEnter: () => reveal.play(), onRefresh: (self) => { if (self.isActive) reveal.play(); } });
      if (hero.getBoundingClientRect().bottom < 0) reveal.progress(1);
    });
  };
  setupNatureMotion();
  document.addEventListener('shopify:section:load', setupNatureMotion);
  const setupOriginEvolution = () => {
    const nature = document.querySelector('#pourquoi-brio')?.closest('.shopify-section');
    const evolved = document.querySelector('#energie-brio')?.closest('.shopify-section');
    if (!nature || !evolved || nature.nextElementSibling !== evolved) return;
    if (window.matchMedia('(min-width: 701px) and (max-width: 1100px), (prefers-reduced-motion: reduce)').matches) return;
    if (document.querySelector('[data-brio-origin-evolution]')) return;

    const chapter = document.createElement('div');
    chapter.className = 'brio-origin-evolution';
    chapter.dataset.brioOriginEvolution = '';
    if (window.innerWidth <= 700) {
      // Keep the pin height fixed while mobile browser chrome expands or collapses.
      chapter.style.setProperty('--brio-origin-viewport-height', `${Math.round(window.visualViewport?.height || window.innerHeight)}px`);
      chapter.style.setProperty('--brio-origin-preview-inset', document.getElementById('PBarNextFrame')
        ? 'calc(64px + env(safe-area-inset-bottom, 0px))'
        : '0px');
    }
    const stage = document.createElement('div');
    stage.className = 'brio-origin-evolution-stage';
    chapter.append(stage);
    nature.before(chapter);
    stage.append(nature, evolved);
    window.ScrollTrigger?.refresh();
  };
  setupOriginEvolution();
  const setupEvolvedScroll = () => {
    const section = document.querySelector('[data-brio-evolved]');
    const track = section?.querySelector('[data-brio-evolved-scroll]');
    const stage = section?.querySelector('.brio-evolved-stage');
    if (!section || !track || !stage || section.dataset.evolvedReady) return;
    section.dataset.evolvedReady = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cta = section.querySelector('.brio-evolved-cta');
    const compareCards = section.querySelector('.brio-evolved-compare');
    const mobileHeroCta = section.querySelector('.brio-evolved-mobile-hero-cta');
    const sources = section.querySelector('.brio-comparison-sources');
    const burstLeaves = [...section.querySelectorAll('[data-brio-leaf-burst] img')];
    const chapter = section.closest('[data-brio-origin-evolution]');
    const nature = chapter?.querySelector('#pourquoi-brio');
    const comparisonItems = [...section.querySelectorAll('.brio-evolved-compare li')];
    const proofItems = [...section.querySelectorAll('.brio-evolved-proof > div')];
    let frame = 0;
    let displayedProgress = null;
    const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
    const segment = (value, start, end) => clamp((value - start) / (end - start));
    const smooth = (value) => value * value * (3 - 2 * value);
    const mix = (from, to, progress) => from + (to - from) * progress;

    const syncMobileViewport = () => {
      if (window.innerWidth > 700) return;
      const height = Math.round(window.visualViewport?.height || window.innerHeight);
      const inset = document.getElementById('PBarNextFrame')
        ? 'calc(64px + env(safe-area-inset-bottom, 0px))'
        : '0px';
      if (stage.style.getPropertyValue('--brio-evolved-viewport-height') !== `${height}px`)
        stage.style.setProperty('--brio-evolved-viewport-height', `${height}px`);
      if (stage.style.getPropertyValue('--brio-evolved-preview-inset') !== inset)
        stage.style.setProperty('--brio-evolved-preview-inset', inset);
    };

    const renderLeafBurst = (progress, compact) => {
      if (!burstLeaves.length || reducedMotion.matches) return;
      const burst = segment(progress, compact ? .49 : .48, compact ? .96 : .95);
      const width = stage.clientWidth;
      const height = stage.clientHeight;

      burstLeaves.forEach((leaf, index) => {
        if (compact && index % 3 === 2) {
          leaf.style.opacity = '0';
          return;
        }
        const naturalDelay = Number(leaf.dataset.delay || 0);
        const wave = index % 6;
        const delay = naturalDelay * .28 + wave * (compact ? .068 : .074);
        const duration = compact ? .58 : .62;
        const p = smooth(segment(burst, delay, Math.min(1, delay + duration)));
        const xStart = width * Number(leaf.dataset.x || 0) / 100;
        const drift = width * Number(leaf.dataset.drift || 0) / 100;
        const phase = Number(leaf.dataset.phase || 0);
        const size = Number(leaf.dataset.size || 90) * (compact ? .68 : 1);
        const fallVariation = height * (Number(leaf.dataset.fall || 100) - 100) / 100;
        const sway = Math.sin(p * Math.PI * 3 + phase) * width * (compact ? .018 : .012);
        const x = xStart + drift * p + sway - size * .5;
        const y = mix(
          -size * 1.35 + Math.min(0, fallVariation),
          height + size * 1.35 + Math.max(0, fallVariation),
          p
        );
        const startRotate = Number(leaf.dataset.rotate || 0);
        const rotation = startRotate + Number(leaf.dataset.spin || 260) * p + Math.sin(p * Math.PI * 2 + phase) * 12;
        const scale = mix(.84, 1.04, p) * (compact ? .82 : 1);
        const fadeIn = segment(p, 0, .08);
        const fadeOut = 1 - segment(p, .77, 1);
        const depthOpacity = leaf.closest('.brio-evolved-leaf-burst--back') ? .62 : .94;

        leaf.style.setProperty('--leaf-size', `${size.toFixed(1)}px`);
        leaf.style.opacity = (fadeIn * fadeOut * depthOpacity).toFixed(3);
        leaf.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0) rotate(${rotation.toFixed(1)}deg) scale(${scale.toFixed(3)})`;
      });
    };

    const render = () => {
      frame = 0;
      syncMobileViewport();
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--brio-header-height')) || 80;
      const travel = Math.max(1, track.offsetHeight - stage.offsetHeight);
      // Finish the reveal before the sticky stage releases so the complete
      // comparison remains visible before the following lifestyle section.
      const revealTravel = travel * (window.innerWidth <= 700 ? .9 : .78);
      const rect = track.getBoundingClientRect();
      const chapterTravel = chapter ? Math.max(1, chapter.offsetHeight - stage.offsetHeight) : 1;
      const chapterProgress = chapter ? clamp((headerHeight - chapter.getBoundingClientRect().top) / chapterTravel) : 0;
      const compact = window.innerWidth <= 700;
      // Mobile holds the plant chapter through a full fast swipe before the
      // next panel can rise. The following gestures still scrub every frame.
      const takeoverStart = compact ? .36 : .12;
      const takeoverEnd = compact ? .50 : .32;
      const takeover = chapter ? smooth(segment(chapterProgress, takeoverStart, takeoverEnd)) : 1;
      if (chapter) {
        chapter.style.setProperty('--takeover-y', `${((1 - takeover) * 100).toFixed(3)}%`);
        chapter.style.setProperty('--nature-brightness', mix(1, .56, takeover).toFixed(3));
        const exit = smooth(segment(chapterProgress, .95, 1));
        chapter.style.setProperty('--evolved-exit-scale', mix(1, .985, exit).toFixed(4));
        chapter.style.setProperty('--evolved-exit-brightness', mix(1, .88, exit).toFixed(3));
      }
      const targetProgress = reducedMotion.matches ? 1 : chapter ? segment(chapterProgress, compact ? .51 : .33, compact ? .91 : .87) : clamp((headerHeight - rect.top) / revealTravel);
      if (displayedProgress === null || reducedMotion.matches || chapter) displayedProgress = targetProgress;
      else displayedProgress += (targetProgress - displayedProgress) * .115;
      if (Math.abs(targetProgress - displayedProgress) < .0005) displayedProgress = targetProgress;
      else frame = requestAnimationFrame(render);
      const progress = displayedProgress;
      if (compact && compareCards && cta) {
        const cardsBottom = compareCards.offsetTop + compareCards.offsetHeight;
        const desiredGap = Math.min(72, Math.max(56, stage.clientHeight * .105));
        const roomAboveSources = stage.clientHeight - cta.offsetHeight - 42;
        const ctaTop = Math.max(cardsBottom + 12, Math.min(cardsBottom + desiredGap, roomAboveSources));
        const value = `${Math.round(ctaTop)}px`;
        if (section.style.getPropertyValue('--brio-compare-cta-top') !== value)
          section.style.setProperty('--brio-compare-cta-top', value);
      }

      let comparison;
      let button;
      if (compact) {
        const classicExit = smooth(segment(progress, .22, .55));
        const classicFade = smooth(segment(progress, .36, .52));
        const brioArrival = smooth(segment(progress, .3, .44));
        const heroArrival = smooth(segment(progress, .43, .62));
        const heroExit = smooth(segment(progress, .7, .82));
        const brioExit = smooth(segment(progress, .73, .85));
        comparison = smooth(segment(progress, .78, .93));
        button = smooth(segment(progress, .87, .98));
        const comparisonHeading = smooth(segment(progress, .7, .82));

        section.style.setProperty('--classic-scale', mix(1, .74, classicExit).toFixed(4));
        section.style.setProperty('--classic-brightness', mix(1, .72, classicExit).toFixed(3));
        section.style.setProperty('--classic-saturation', mix(1, .75, classicExit).toFixed(3));
        section.style.setProperty('--classic-opacity', (1 - classicFade).toFixed(3));
        section.style.setProperty('--classic-x', `${mix(0, -19, classicExit).toFixed(2)}vw`);
        section.style.setProperty('--classic-y', '0px');
        section.style.setProperty('--classic-label-opacity', (1 - smooth(segment(progress, .14, .34))).toFixed(3));
        section.style.setProperty('--classic-label-y', `${mix(0, -12, classicExit).toFixed(2)}px`);
        section.style.setProperty('--brio-y', `${(mix(70, 0, brioArrival) - 54 * brioExit).toFixed(2)}px`);
        section.style.setProperty('--brio-scale', (mix(.94, 1, brioArrival) * mix(1, .74, brioExit)).toFixed(4));
        section.style.setProperty('--brio-rotate', '0deg');
        section.style.setProperty('--brio-opacity', (brioArrival * (1 - brioExit)).toFixed(3));
        section.style.setProperty('--title-opacity', (heroArrival * (1 - heroExit)).toFixed(3));
        section.style.setProperty('--title-y', `${(mix(14, 0, heroArrival) - 18 * heroExit).toFixed(2)}px`);
        section.style.setProperty('--compare-opacity', comparison.toFixed(3));
        section.style.setProperty('--compare-y', `${mix(24, 0, comparison).toFixed(2)}px`);
        section.style.setProperty('--compare-heading-opacity', comparisonHeading.toFixed(3));
        section.style.setProperty('--compare-heading-y', `${mix(15, 0, comparisonHeading).toFixed(2)}px`);
        section.style.setProperty('--cta-opacity', button.toFixed(3));
        section.style.setProperty('--cta-y', `${mix(16, 0, button).toFixed(2)}px`);
        section.style.setProperty('--hint-opacity', '0');
        section.style.setProperty('--scene-scale', mix(1.015, 1.03, progress).toFixed(4));
        section.style.setProperty('--mobile-bg-brightness', mix(.82, 1, brioArrival) * mix(1, .72, comparison));
      } else {
        const classic = segment(progress, chapter ? .24 : 0, chapter ? .69 : .56);
        const brio = smooth(segment(progress, chapter ? .46 : .28, chapter ? .73 : .66));
        const brioOpacity = smooth(segment(progress, chapter ? .46 : .28, chapter ? .58 : .44));
        const title = smooth(segment(progress, chapter ? .08 : .56, chapter ? .28 : .71));
        comparison = smooth(segment(progress, chapter ? .72 : .69, chapter ? .91 : .87));
        button = smooth(segment(progress, chapter ? .86 : .8, chapter ? .96 : .91));

        section.style.setProperty('--classic-scale', mix(1.75, .9, classic).toFixed(4));
        const classicExit = 1 - smooth(segment(progress, .69, .82));
        section.style.setProperty('--classic-opacity', (mix(1, .38, classic) * classicExit * (chapter ? smooth(segment(chapterProgress, .29, .37)) : 1)).toFixed(3));
        section.style.setProperty('--classic-y', `${mix(0, -112, classic).toFixed(2)}px`);
        section.style.setProperty('--classic-x', `${mix(0, -8, classic).toFixed(2)}vw`);
        section.style.setProperty('--brio-y', `${mix(195, 0, brio).toFixed(2)}px`);
        section.style.setProperty('--brio-scale', mix(.85, 1, brio).toFixed(4));
        section.style.setProperty('--brio-rotate', `${mix(4.5, 0, brio).toFixed(3)}deg`);
        section.style.setProperty('--brio-opacity', brioOpacity.toFixed(3));
        const visibleTitle = !chapter && window.innerWidth <= 1100
          ? title * (1 - smooth(segment(progress, .64, .75)))
          : title;
        section.style.setProperty('--title-opacity', visibleTitle.toFixed(3));
        section.style.setProperty('--title-y', `${mix(24, 0, title).toFixed(2)}px`);
        section.style.setProperty('--title-reveal', smooth(segment(progress, chapter ? .17 : .56, chapter ? .35 : .71)).toFixed(3));
        if (chapter) {
          const description = smooth(segment(progress, .31, .44));
          section.style.setProperty('--description-opacity', description.toFixed(3));
          section.style.setProperty('--description-y', `${mix(10, 0, description).toFixed(2)}px`);
          section.style.setProperty('--evolved-scene-brightness', mix(.72, 1, smooth(segment(progress, .22, .68))).toFixed(3));
        }
        section.style.setProperty('--compare-opacity', comparison.toFixed(3));
        section.style.setProperty('--compare-y', `${mix(28, 0, comparison).toFixed(2)}px`);
        section.style.setProperty('--cta-opacity', button.toFixed(3));
        section.style.setProperty('--cta-y', `${mix(18, 0, button).toFixed(2)}px`);
        section.style.setProperty('--hint-opacity', (1 - smooth(segment(progress, .02, .15))).toFixed(3));
        section.style.setProperty('--scene-scale', mix(1.015, 1.035, progress).toFixed(4));
        section.style.setProperty('--mobile-bg-brightness', '1');
      }
      renderLeafBurst(progress, compact);
      if (chapter) {
        comparisonItems.forEach((item, index) => {
          const itemProgress = smooth(segment(progress, .72 + index * .012, .87 + index * .012));
          item.style.opacity = itemProgress.toFixed(3);
          item.style.transform = `translateY(${((1 - itemProgress) * 10).toFixed(2)}px)`;
        });
        proofItems.forEach((item, index) => {
          const itemProgress = smooth(segment(progress, .85 + index * .025, .95 + index * .025));
          item.style.opacity = itemProgress.toFixed(3);
          item.style.transform = `translateY(${((1 - itemProgress) * 10).toFixed(2)}px)`;
        });
      }
      section.dataset.progress = progress.toFixed(3);

      const interactive = reducedMotion.matches || button > .8;
      cta?.toggleAttribute('inert', !interactive);
      mobileHeroCta?.toggleAttribute('inert', !compact || progress < .6 || progress > .77);
      sources?.toggleAttribute('inert', !interactive);
    };
    const requestRender = () => {
      if (!frame) frame = requestAnimationFrame(render);
    };

    render();
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
    window.visualViewport?.addEventListener('resize', requestRender, { passive: true });
    reducedMotion.addEventListener?.('change', requestRender);
  };
  setupEvolvedScroll();
  document.addEventListener('shopify:section:load', setupEvolvedScroll);
  const setupScrollLeaves = () => {
    const layer = document.querySelector('[data-brio-scroll-leaves]');
    if (!layer || layer.dataset.ready) return;
    layer.dataset.ready = 'true';

    const leaves = [...layer.querySelectorAll('.brio-scroll-leaf')];
    const zones = [...document.querySelectorAll('#pourquoi-brio, #energie-brio, #brio-au-quotidien')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!leaves.length || !zones.length) return;

    const active = new Map();
    let previousY = window.scrollY;
    let travelled = 0;
    let lastSpawnAt = -Infinity;
    let previousZone = null;
    let frame = 0;
    let calmTimer = 0;
    let natureTimer = 0;
    let arrivalTimer = 0;
    let natureLaunchCount = 0;

    const mobile = () => window.innerWidth <= 700;
    const limit = (zone) => zone?.id === 'pourquoi-brio' ? (mobile() ? 5 : 6) : (mobile() ? 2 : 4);
    const natureIntensity = (zone) => {
      const top = zone.getBoundingClientRect().top;
      return Math.min(1, Math.max(0, (window.innerHeight * .88 - top) / (window.innerHeight * .9)));
    };
    const visibleZone = () => {
      const viewportHeight = window.innerHeight;
      const nature = zones[0];
      const natureRect = nature?.getBoundingClientRect();
      if (mobile() && natureRect && natureRect.top < viewportHeight * .88 && natureRect.bottom > viewportHeight * .28) {
        return nature;
      }
      const evolvedStage = document.querySelector('#energie-brio .brio-evolved-stage');
      const evolvedRect = evolvedStage?.getBoundingClientRect();
      if (evolvedRect && evolvedRect.top < viewportHeight * .83 && evolvedRect.bottom > viewportHeight * .18) {
        return document.querySelector('#energie-brio');
      }
      return zones.find((zone) => {
        const rect = zone.getBoundingClientRect();
        return rect.top < viewportHeight * .83 && rect.bottom > viewportHeight * .18;
      });
    };
    const reset = () => {
      active.forEach(({ animation }) => animation.cancel());
      active.clear();
      leaves.forEach((leaf) => leaf.classList.remove('is-falling'));
      window.clearTimeout(natureTimer);
      window.clearTimeout(arrivalTimer);
      previousZone = null;
      travelled = 0;
      natureLaunchCount = 0;
    };
    const freeLeaf = () => leaves.find((leaf) => !active.has(leaf));
    const random = (min, max) => min + Math.random() * (max - min);

    const release = (leaf, animation) => {
      const record = active.get(leaf);
      if (!record || record.animation !== animation) return;
      active.delete(leaf);
      leaf.classList.remove('is-falling');
      leaf.removeAttribute('style');
    };

    const launch = (zone) => {
      if (reducedMotion.matches || active.size >= limit(zone)) return;
      const leaf = freeLeaf();
      if (!leaf) return;

      const compact = mobile();
      const nature = zone.id === 'pourquoi-brio';
      const quiet = zone.id === 'energie-brio';
      const edgeLanes = quiet ? [-5, 91] : compact && nature ? [2, 8, 14, 78, 85, 91] : [-4, 5, 13, 76, 87, 95];
      const startX = compact && nature && natureLaunchCount < 2
        ? [82, 8][natureLaunchCount]
        : edgeLanes[Math.floor(Math.random() * edgeLanes.length)];
      if (nature) natureLaunchCount += 1;
      const size = compact ? random(nature ? 30 : 34, nature ? 50 : 57) : random(58, quiet ? 88 : 116);
      const drift = random(compact ? -24 : -70, compact ? 24 : 70);
      const sway = random(compact ? 7 : 16, compact ? 18 : 42) * (Math.random() < .5 ? -1 : 1);
      const startRotation = random(-38, 38);
      const turn = random(115, 265) * (Math.random() < .5 ? -1 : 1);
      const blur = quiet ? random(.6, 2.1) : random(0, nature ? 1.8 : 3.2);
      const opacity = quiet ? random(.32, .46) : random(nature ? .54 : .46, nature ? .72 : .68);
      const duration = nature ? (compact ? random(3300, 4300) : random(4200, 5600)) : compact ? random(5200, 6800) : random(5900, 8200);

      leaf.style.setProperty('--leaf-x', `${startX}vw`);
      leaf.style.setProperty('--leaf-size', `${size}px`);
      leaf.style.setProperty('--leaf-opacity', opacity.toFixed(2));
      leaf.style.setProperty('--leaf-blur', `${blur.toFixed(1)}px`);
      leaf.classList.add('is-falling');

      const keyframes = [
        { transform: `translate3d(0,${nature ? '-7vh' : '-18vh'},0) rotate(${startRotation}deg)`, opacity: 0 },
        { transform: `translate3d(${sway}px,${nature ? '12vh' : '4vh'},0) rotate(${startRotation + turn * .18}deg)`, opacity, offset: nature ? .12 : .16 },
        { transform: `translate3d(${drift * .45}px,48vh,0) rotate(${startRotation + turn * .58}deg)`, opacity: opacity * .92, offset: .58 },
        { transform: `translate3d(${drift}px,112vh,0) rotate(${startRotation + turn}deg)`, opacity: 0 }
      ];
      const animation = leaf.animate(keyframes, {
        duration,
        easing: 'cubic-bezier(.22,.45,.28,.98)',
        fill: 'forwards'
      });
      active.set(leaf, { animation });
      animation.onfinish = () => release(leaf, animation);
      animation.oncancel = () => release(leaf, animation);
    };

    const scheduleNatureLeaves = (zone) => {
      window.clearTimeout(natureTimer);
      if (visibleZone() !== zone || reducedMotion.matches) return;
      const delay = 900 - natureIntensity(zone) * 480;
      natureTimer = window.setTimeout(() => {
        if (visibleZone() !== zone || reducedMotion.matches) return;
        launch(zone);
        scheduleNatureLeaves(zone);
      }, delay);
    };

    const update = () => {
      frame = 0;
      if (reducedMotion.matches) {
        reset();
        previousY = window.scrollY;
        return;
      }

      const now = performance.now();
      const y = window.scrollY;
      const delta = Math.abs(y - previousY);
      previousY = y;
      travelled += delta;
      const zone = visibleZone();

      if (!zone) {
        window.clearTimeout(natureTimer);
        window.clearTimeout(arrivalTimer);
        previousZone = null;
        travelled = 0;
        return;
      }

      const justEntered = zone !== previousZone;
      if (justEntered) {
        window.clearTimeout(natureTimer);
        window.clearTimeout(arrivalTimer);
      }
      if (justEntered && previousZone && (zone.id === 'energie-brio' || previousZone.id === 'energie-brio')) {
        active.forEach(({ animation }) => animation.cancel());
        active.clear();
      }
      const threshold = mobile() ? 300 : 190;
      const cooldown = mobile() ? 1350 : 820;
      if (justEntered && zone.id === 'pourquoi-brio') {
        natureLaunchCount = 0;
        launch(zone);
        arrivalTimer = window.setTimeout(() => {
          if (visibleZone() === zone) launch(zone);
        }, mobile() ? 180 : 260);
        scheduleNatureLeaves(zone);
        travelled = 0;
        lastSpawnAt = now;
      } else if (zone.id === 'pourquoi-brio' && travelled >= (mobile() ? 80 : 120) && now - lastSpawnAt > (mobile() ? 320 : 430)) {
        launch(zone);
        travelled = 0;
        lastSpawnAt = now;
      } else if ((justEntered || travelled >= threshold) && now - lastSpawnAt > cooldown) {
        launch(zone);
        if (justEntered && !mobile() && zone.id !== 'energie-brio' && active.size < limit(zone)) {
          window.setTimeout(() => {
            if (visibleZone() === zone) launch(zone);
          }, 420);
        }
        travelled = 0;
        lastSpawnAt = now;
      }
      previousZone = zone;

      const natureSpeed = zone.id === 'pourquoi-brio' ? .96 + natureIntensity(zone) * .3 : .82;
      const speed = Math.min(zone.id === 'pourquoi-brio' ? 1.65 : 1.35, natureSpeed + delta / 65);
      active.forEach(({ animation }) => { animation.playbackRate = speed; });
      window.clearTimeout(calmTimer);
      calmTimer = window.setTimeout(() => {
        active.forEach(({ animation }) => { animation.playbackRate = natureSpeed; });
      }, 140);
    };
    const requestUpdate = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    reducedMotion.addEventListener?.('change', requestUpdate);
    requestUpdate();
  };
  setupScrollLeaves();
  document.addEventListener('shopify:section:load', setupScrollLeaves);
  const setupFlavourMotion = () => {
    document.querySelectorAll('.brio-editorial-flavours').forEach((section) => {
      if (section.dataset.flavourMotionReady) return;
      section.dataset.flavourMotionReady = 'true';
      section.classList.add('brio-flavours-motion-ready');

      const cards = [...section.querySelectorAll('[data-brio-flavour-card]')];
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      const compact = window.innerWidth <= 600;
      const hero = document.querySelector('.brio-hero-layered');
      if (!cards.length) return;

      cards.forEach((card, index) => card.style.setProperty('--reveal-delay', `${index * (compact ? 80 : 110)}ms`));

      const reveal = () => {
        cards.forEach((card) => card.classList.add('is-revealed'));
        if (!reducedMotion.matches && window.innerWidth > 900) hero?.classList.add('is-flavours-transitioning');
      };
      if ('IntersectionObserver' in window) {
        const revealObserver = new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          reveal();
          observer.disconnect();
        }, { threshold: compact ? .08 : .17, rootMargin: '0px' });
        revealObserver.observe(section);
      } else {
        reveal();
      }

      const centerObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const active = window.innerWidth <= 600 && entry.isIntersecting && !reducedMotion.matches;
          entry.target.classList.toggle('is-mobile-active', active);
        });
      }, { threshold: .46, rootMargin: '-22% 0px -22% 0px' }) : null;
      cards.forEach((card) => centerObserver?.observe(card));

      cards.forEach((card) => {
        let frame = 0;
        let pointerX = 0;
        let pointerY = 0;
        const renderPointer = () => {
          frame = 0;
          card.style.setProperty('--photo-x', `${(pointerX * 1.25).toFixed(2)}px`);
          card.style.setProperty('--photo-y', `${(pointerY * .8).toFixed(2)}px`);
          card.style.setProperty('--can-shift-x', `${(pointerX * 2.15).toFixed(2)}px`);
          card.style.setProperty('--can-shift-y', `${(pointerY * 1.35).toFixed(2)}px`);
        };
        card.addEventListener('pointermove', (event) => {
          if (!finePointer.matches || reducedMotion.matches) return;
          const rect = card.getBoundingClientRect();
          pointerX = ((event.clientX - rect.left) / rect.width - .5) * 2;
          pointerY = ((event.clientY - rect.top) / rect.height - .5) * 2;
          if (!frame) frame = requestAnimationFrame(renderPointer);
        }, { passive: true });
        card.addEventListener('pointerleave', () => {
          if (frame) cancelAnimationFrame(frame);
          frame = 0;
          card.style.removeProperty('--photo-x');
          card.style.removeProperty('--photo-y');
          card.style.removeProperty('--can-shift-x');
          card.style.removeProperty('--can-shift-y');
        });
      });

      const refreshMobileState = () => {
        if (window.innerWidth > 600 || reducedMotion.matches) cards.forEach((card) => card.classList.remove('is-mobile-active'));
      };
      window.addEventListener('resize', refreshMobileState, { passive: true });
      reducedMotion.addEventListener?.('change', refreshMobileState);
    });
  };
  setupFlavourMotion();
  document.addEventListener('shopify:section:load', setupFlavourMotion);
  const setupPackFeatureMotion = () => {
    document.querySelectorAll('.pack-discovery').forEach((section) => {
      if (section.dataset.packMotionReady) return;
      section.dataset.packMotionReady = 'true';
      const media = section.querySelector('.pack-visual');
      const cans = [...section.querySelectorAll('.pack-can')];
      const eyebrow = section.querySelector('.pack-eyebrow');
      const title = section.querySelector('.pack-title');
      const description = section.querySelector('.pack-description');
      const stats = section.querySelector('.pack-stats');
      const cta = section.querySelector('.pack-cta');
      const ctaArrow = section.querySelector('.pack-cta-arrow');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      const revealTargets = [eyebrow, title, description, stats, cta].filter(Boolean);

      if (!window.gsap || !window.ScrollTrigger) {
        section.classList.add('brio-pack-motion-ready');
        requestAnimationFrame(() => section.classList.add('is-pack-revealed'));
        return;
      }

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      section.classList.add('brio-pack-gsap-ready');

      if (reducedMotion.matches) {
        gsap.set([...cans, ...revealTargets], { clearProps: 'transform,opacity,visibility' });
        return;
      }

      gsap.set(cans, { y: 65, scale: .96, opacity: 0 });
      gsap.set(revealTargets, { y: 18, opacity: 0 });

      const packTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top 72%',
          once: true
        }
      });

      packTimeline
        .to(cans, {
          y: 0,
          scale: 1,
          opacity: 1,
          duration: .65,
          stagger: window.innerWidth <= 850 ? .085 : .12,
          ease: 'power3.out'
        })
        .to(eyebrow, { y: 0, opacity: 1, duration: .4 }, '-=.15')
        .to(title, { y: 0, opacity: 1, duration: .55, ease: 'power2.out' }, '-=.2')
        .to(description, { y: 0, opacity: 1, duration: .4 }, '-=.25')
        .to(stats, { y: 0, opacity: 1, duration: .45, ease: 'power2.out' }, '-=.15')
        .to(cta, {
          y: 0,
          opacity: 1,
          duration: .45,
          ease: 'power2.out',
          onStart: () => section.classList.add('is-pack-cta-shining')
        }, '-=.1');

      if (!finePointer.matches) return;
      cans.forEach((can) => {
        can.addEventListener('mouseenter', () => gsap.to(can, { y: -6, scale: 1.015, duration: .3, ease: 'power2.out', overwrite: 'auto' }));
        can.addEventListener('mouseleave', () => gsap.to(can, { y: 0, scale: 1, duration: .4, ease: 'power2.out', overwrite: 'auto' }));
      });
      cta?.addEventListener('mouseenter', () => gsap.to(ctaArrow, { x: 6, duration: .25, overwrite: 'auto' }));
      cta?.addEventListener('mouseleave', () => gsap.to(ctaArrow, { x: 0, duration: .3, overwrite: 'auto' }));
    });
  };
  setupPackFeatureMotion();
  document.addEventListener('shopify:section:load', setupPackFeatureMotion);
  const setupLifeStory = () => {
    document.querySelectorAll('[data-brio-life]').forEach((section) => {
      if (section.dataset.lifeReady) return;
      section.dataset.lifeReady = 'true';
      section.closest('.shopify-section')?.classList.add('brio-life-chapter');

      const scene = section.querySelector('.brio-life-scene');
      const panels = section.querySelector('.brio-life-panels');
      const cards = [...section.querySelectorAll('.brio-life-card')];
      const buttons = [...section.querySelectorAll('[data-life-go]')];
      const progressFill = section.querySelector('.brio-life-progress > span');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let desktopTimeline = null;
      let mobileFrame = 0;

      const setMoment = (index) => {
        cards.forEach((card, cardIndex) => card.classList.toggle('is-mobile-active', cardIndex === index));
        buttons.forEach((button, buttonIndex) => {
          button.classList.toggle('is-active', buttonIndex === index);
          if (buttonIndex === index) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        if (window.innerWidth <= 760 && progressFill) progressFill.style.transform = 'scaleX(' + ((index + 1) / 4) + ')';
      };
      setMoment(0);

      const syncMobileMoment = () => {
        mobileFrame = 0;
        if (window.innerWidth > 760 || !panels) return;
        const viewportCenter = panels.getBoundingClientRect().left + panels.clientWidth / 2;
        let closest = 0;
        let distance = Infinity;
        cards.forEach((card, index) => {
          const bounds = card.getBoundingClientRect();
          const candidate = Math.abs(bounds.left + bounds.width / 2 - viewportCenter);
          if (candidate < distance) { distance = candidate; closest = index; }
        });
        setMoment(closest);
      };
      panels?.addEventListener('scroll', () => {
        if (window.innerWidth <= 760 && !mobileFrame) mobileFrame = requestAnimationFrame(syncMobileMoment);
      }, { passive: true });

      buttons.forEach((button, index) => button.addEventListener('click', () => {
        if (window.innerWidth > 760 && desktopTimeline?.scrollTrigger) {
          const trigger = desktopTimeline.scrollTrigger;
          const positions = [.18, .34, .52, .7];
          window.scrollTo({ top: trigger.start + (trigger.end - trigger.start) * positions[index], behavior: 'smooth' });
          return;
        }
        const card = cards[index];
        if (!card || !panels) return;
        const target = card.getBoundingClientRect().left - panels.getBoundingClientRect().left + panels.scrollLeft - 18;
        panels.scrollTo({ left: target, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        setMoment(index);
      }));

      if (!window.gsap || !window.ScrollTrigger || reducedMotion.matches) {
        section.classList.add('is-life-static');
        return;
      }

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      const media = gsap.matchMedia();
      media.add('(min-width: 761px) and (prefers-reduced-motion: no-preference)', () => {
        section.classList.add('is-life-animated');
        const eyebrow = section.querySelector('.brio-life-eyebrow');
        const headingLines = [...section.querySelectorAll('.brio-life-heading-line > span')];
        const subtitle = section.querySelector('.brio-life-intro');
        const images = cards.map((card) => card.querySelector('img'));
        const titles = cards.map((card) => card.querySelector('.brio-life-title-mask > span'));
        const handwritten = cards.map((card) => card.querySelector('.brio-life-card-handwritten'));
        const finale = section.querySelector('.brio-life-finale');
        const finaleEyebrow = finale.querySelector('.brio-life-finale-eyebrow');
        const finaleTitle = finale.querySelector('h3');
        const finaleBenefits = [...finale.querySelectorAll('.brio-brand-benefits__item')];
        const finaleCta = finale.querySelector('.brio-life-cta');
        const headerHeight = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--brio-header-height')) || 88;

        gsap.set(eyebrow, { autoAlpha: 0, y: 12 });
        gsap.set(headingLines, { yPercent: 110 });
        gsap.set(subtitle, { autoAlpha: 0, y: 14 });
        gsap.set(cards, { autoAlpha: 0, y: 20, scale: 1 });
        gsap.set(images, { scale: 1.02 });
        gsap.set(titles, { yPercent: 110 });
        gsap.set(handwritten, { autoAlpha: 0, y: 10 });
        gsap.set(progressFill, { scaleX: 0 });
        gsap.set(finale, { autoAlpha: 0, y: 12 });
        gsap.set([finaleEyebrow, finaleTitle, finaleCta, ...finaleBenefits], { autoAlpha: 0, y: 12 });

        // Reveal the heading and START while the cream section enters the
        // viewport, before the pinned scene reaches the header.
        const openingTimeline = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            id: 'brio-life-opening-' + (section.id || 'story'),
            trigger: section,
            start: 'top bottom-=80',
            end: 'top 58%',
            scrub: .25,
            invalidateOnRefresh: true
          }
        });
        openingTimeline
          .to(eyebrow, { autoAlpha: 1, y: 0, duration: .18 }, 0)
          .to(headingLines, { yPercent: 0, duration: .36, stagger: .06 }, .1)
          .to(subtitle, { autoAlpha: 1, y: 0, duration: .2 }, .4)
          .to(cards[0], { autoAlpha: 1, y: 0, duration: .25 }, .5)
          .to(images[0], { scale: 1, duration: .25 }, .5)
          .to(titles[0], { yPercent: 0, duration: .2 }, .62)
          .to(progressFill, { scaleX: .25, duration: .18 }, .62)
          .to(handwritten[0], { autoAlpha: 1, y: 0, duration: .12 }, .78);

        const timeline = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            id: 'brio-life-' + (section.id || 'story'),
            trigger: scene,
            start: () => 'top top+=' + headerHeight(),
            end: () => '+=' + Math.round(Math.min(2050, Math.max(1450, window.innerHeight * 1.85))),
            pin: true,
            scrub: .55,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              const p = self.progress;
              setMoment(p < .27 ? 0 : p < .45 ? 1 : p < .64 ? 2 : 3);
            }
          }
        });
        desktopTimeline = timeline;

        const changeScene = (previous, next, at) => {
          timeline
            .to(titles[previous], { yPercent: -110, duration: .2 }, at)
            .to(handwritten[previous], { autoAlpha: 0, y: -8, duration: .16 }, at)
            .to(images[previous], { scale: .985, duration: .26 }, at)
            .to(cards[previous], { autoAlpha: 0, scale: .985, duration: .26 }, at)
            .fromTo(cards[next], { autoAlpha: 0, y: 20, scale: 1 }, { autoAlpha: 1, y: 0, duration: .26 }, at + .12)
            .to(images[next], { scale: 1, duration: .3 }, at + .12)
            .to(titles[next], { yPercent: 0, duration: .23 }, at + .25)
            .to(progressFill, { scaleX: (next + 1) / 4, duration: .32 }, at + .16)
            .to(handwritten[next], { autoAlpha: 1, y: 0, duration: .18 }, at + .43);
        };
        changeScene(0, 1, 1.14);
        changeScene(1, 2, 2.1);
        changeScene(2, 3, 3.06);

        timeline
          .to(cards[3], { autoAlpha: .18, scale: .985, duration: .28 }, 4.03)
          .to(finale, { autoAlpha: 1, y: 0, duration: .28 }, 4.05)
          .to(finaleEyebrow, { autoAlpha: 1, y: 0, duration: .15 }, 4.2)
          .to(finaleTitle, { autoAlpha: 1, y: 0, duration: .25 }, 4.28)
          .to(finaleBenefits, { autoAlpha: 1, y: 0, duration: .18, stagger: .07 }, 4.55)
          .to(finaleCta, { autoAlpha: 1, y: 0, duration: .21 }, 4.82)
          .to({}, { duration: .35 });

        requestAnimationFrame(() => ScrollTrigger.refresh());
        return () => {
          desktopTimeline = null;
          section.classList.remove('is-life-animated');
          openingTimeline.scrollTrigger?.kill();
          openingTimeline.kill();
          timeline.scrollTrigger?.kill();
          timeline.kill();
          gsap.set([eyebrow, ...headingLines, subtitle, ...cards, ...images, ...titles, ...handwritten, progressFill, finale, finaleEyebrow, finaleTitle, ...finaleBenefits, finaleCta], { clearProps: 'all' });
        };
      });

      media.add('(max-width: 760px) and (prefers-reduced-motion: no-preference)', () => {
        section.classList.add('is-life-mobile-animated');
        const eyebrow = section.querySelector('.brio-life-eyebrow');
        const headingLines = [...section.querySelectorAll('.brio-life-heading-line > span')];
        const intro = section.querySelector('.brio-life-intro');
        const openingLeaf = section.querySelector('.brio-life-opening img');
        const nav = section.querySelector('.brio-life-nav');
        const finale = section.querySelector('.brio-life-finale');

        const entrance = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: section,
            start: 'top 95%',
            end: 'top 38%',
            scrub: .25,
            invalidateOnRefresh: true
          }
        });
        entrance
          .fromTo(openingLeaf, { y: -14, rotation: -12, opacity: 0 }, { y: 0, rotation: 0, opacity: .55, duration: .35 }, 0)
          .fromTo(eyebrow, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: .22 }, .08)
          .fromTo(headingLines[0], { yPercent: 110 }, { yPercent: 0, duration: .34 }, .13)
          .fromTo(headingLines[1], { yPercent: 110 }, { yPercent: 0, duration: .34 }, .23)
          .fromTo(intro, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: .28 }, .43)
          .fromTo(panels, { autoAlpha: 0, y: 34, scale: .985 }, { autoAlpha: 1, y: 0, scale: 1, duration: .48 }, .36)
          .fromTo(nav, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: .24 }, .72);

        gsap.fromTo(finale,
          { opacity: .45, y: 22 },
          {
            opacity: 1,
            y: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: finale,
              start: 'top 90%',
              end: 'top 58%',
              scrub: .25,
              invalidateOnRefresh: true
            }
          }
        );

        return () => section.classList.remove('is-life-mobile-animated');
      });

      const onUnload = (event) => {
        if (!event.target.contains(section)) return;
        media.revert();
        if (mobileFrame) cancelAnimationFrame(mobileFrame);
        document.removeEventListener('shopify:section:unload', onUnload);
      };
      document.addEventListener('shopify:section:unload', onUnload);
    });
  };
  setupLifeStory();
  document.addEventListener('shopify:section:load', setupLifeStory);
  const setupStoreLocatorMotion = () => {
    document.querySelectorAll('[data-brio-locator]').forEach((section) => {
      if (section.dataset.locatorMotionReady) return;
      section.dataset.locatorMotionReady = 'true';

      const badge = section.querySelector('.brio-locator-badge');
      const title = section.querySelector('.brio-locator-copy h2');
      const description = section.querySelector('.brio-locator-description');
      const logos = [...section.querySelectorAll('.brio-locator-logos > img, .brio-locator-logos-bottom > *')];
      const promises = [...section.querySelectorAll('.brio-locator-promises > div')];
      const mapShell = section.querySelector('.brio-locator-map-shell');
      const locateButton = section.querySelector('.brio-locate-me');
      const panel = section.querySelector('.brio-locator-panel');
      const field = section.querySelector('.brio-locator-input');
      const submit = section.querySelector('.brio-locator-submit');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (reducedMotion.matches || !window.gsap || !window.ScrollTrigger) {
        section.classList.add('is-locator-pins-visible');
        return;
      }

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      const compact = window.innerWidth <= 760;
      const copyTargets = [badge, title, description].filter(Boolean);
      const formTargets = [field, submit].filter(Boolean);

      // Keep the locator usable immediately, including when navigation jumps straight to it.
      gsap.set(copyTargets, { y: compact ? 5 : 8 });
      gsap.set(logos, { y: 6 });
      gsap.set(promises, { y: 6 });
      gsap.set(mapShell, { y: 12, scale: .99, transformOrigin: '50% 54%' });
      gsap.set(panel, { x: compact ? 8 : 12 });
      gsap.set(locateButton, { y: 6 });
      gsap.set(formTargets, { y: 6 });
      section.classList.add('brio-locator-motion-ready', 'is-locator-pins-visible');

      const timeline = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: {
          trigger: section,
          start: 'top 90%',
          once: true
        }
      });

      timeline
        .to(badge, { y: 0, duration: .25 }, 0)
        .to(title, { y: 0, duration: .32 }, .04)
        .to(description, { y: 0, duration: .3 }, .08)
        .to(logos, { y: 0, duration: .28, stagger: .035 }, .1)
        .to(promises, { y: 0, duration: .28, stagger: .035 }, .13)
        .to(mapShell, { y: 0, scale: 1, duration: .42, ease: 'power3.out' }, .02)
        .to(panel, { x: 0, duration: .38, ease: 'power3.out' }, .08)
        .to(locateButton, { y: 0, duration: .28 }, .13)
        .to(field, { y: 0, duration: .28 }, .17)
        .to(submit, { y: 0, duration: .28 }, .2);
    });
  };
  setupStoreLocatorMotion();
  document.addEventListener('shopify:section:load', setupStoreLocatorMotion);
  const setupCommunityMotion = () => {
    document.querySelectorAll('[data-brio-community]').forEach((section) => {
      if (section.dataset.communityMotionReady) return;
      section.dataset.communityMotionReady = 'true';
      section.classList.add('brio-community-motion-ready');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        section.classList.add('is-community-visible');
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        section.classList.add('is-community-visible');
        observer.disconnect();
      }, { threshold: .13, rootMargin: '0px 0px -8% 0px' });
      observer.observe(section);
    });
  };
  setupCommunityMotion();
  document.addEventListener('shopify:section:load', setupCommunityMotion);
  const setupUniverseMotion = () => {
    document.querySelectorAll('[data-brio-universe]').forEach((section) => {
      if (section.dataset.universeMotionReady) return;
      section.dataset.universeMotionReady = 'true';

      const eyebrow = section.querySelector('.brio-universe-eyebrow');
      const titleLines = [...section.querySelectorAll('.brio-universe-title-line > span')];
      const titleRule = section.querySelector('.brio-universe-heading h2 > i');
      const intro = section.querySelector('.brio-universe-intro');
      const signature = section.querySelector('.brio-universe-signature');
      const signatureRule = section.querySelector('.brio-universe-signature > i');
      const cards = [...section.querySelectorAll('.brio-universe-card')];
      const images = cards.map((card) => card.querySelector(':scope > img'));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (reducedMotion.matches || !window.gsap || !window.ScrollTrigger) {
        section.classList.add('is-universe-revealed');
        return;
      }

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      const compact = window.innerWidth <= 760;

      gsap.set(eyebrow, { autoAlpha: 0, y: 12 });
      gsap.set(titleLines, { yPercent: 112 });
      gsap.set(titleRule, { scaleX: 0, transformOrigin: 'left center' });
      gsap.set(intro, { autoAlpha: 0, y: 16 });
      gsap.set(signature, { autoAlpha: 0, y: 13, rotation: -13 });
      gsap.set(signatureRule, { scaleX: 0, transformOrigin: 'left center' });
      gsap.set(cards, { autoAlpha: 0, y: compact ? 20 : 28, scale: .985 });
      gsap.set(images, { scale: compact ? 1.012 : 1.02 });

      gsap.timeline({
        defaults: { ease: 'power3.out' },
        scrollTrigger: { trigger: section, start: 'top 92%', once: true },
        onComplete: () => {
          gsap.set([eyebrow, intro, signature], { clearProps: 'opacity,visibility,transform' });
          gsap.set(titleLines, { clearProps: 'transform' });
          gsap.set([titleRule, signatureRule], { clearProps: 'transform,transformOrigin' });
          gsap.set(cards, { clearProps: 'opacity,visibility,transform' });
          gsap.set(images, { clearProps: 'transform' });
          section.classList.add('is-universe-revealed');
        }
      })
        .to(eyebrow, { autoAlpha: 1, y: 0, duration: .24 }, 0)
        .to(titleLines, { yPercent: 0, duration: .4, stagger: compact ? .05 : .07 }, .04)
        .to(titleRule, { scaleX: 1, duration: .3 }, .24)
        .to(intro, { autoAlpha: 1, y: 0, duration: .32 }, .16)
        .to(signature, { autoAlpha: 1, y: 0, rotation: -11, duration: .36 }, .2)
        .to(signatureRule, { scaleX: 1, duration: .28 }, .3)
        .to(cards, { autoAlpha: 1, y: 0, scale: 1, duration: .46, stagger: compact ? .05 : .07 }, .14)
        .to(images, { scale: 1, duration: .56, stagger: compact ? .05 : .07 }, .14);
    });
  };
  setupUniverseMotion();
  document.addEventListener('shopify:section:load', setupUniverseMotion);
  const setupMoreBrioMotion = () => {
    document.querySelectorAll('[data-brio-more]').forEach((section) => {
      if (section.dataset.moreBrioMotionReady) return;
      section.dataset.moreBrioMotionReady = 'true';

      const panel = section.querySelector('.brio-more-panel');
      const title = section.querySelector('.brio-more-copy h2');
      const intro = section.querySelector('.brio-more-intro');
      const form = section.querySelector('#brio-newsletter');
      const image = section.querySelector('.brio-more-image');
      const signature = section.querySelector('.brio-more-community');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!panel || reducedMotion.matches || !window.gsap || !window.ScrollTrigger) return;

      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);
      const compact = window.innerWidth <= 800;

      gsap.set(panel, { autoAlpha: 0, y: compact ? 22 : 34 });
      gsap.set(title, { autoAlpha: 0, y: 20, clipPath: 'inset(0 0 100% 0)' });
      gsap.set(intro, { autoAlpha: 0, y: 14 });
      gsap.set(form, { autoAlpha: 0, y: 16 });
      gsap.set(signature, { autoAlpha: 0, y: 12 });

      gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: { trigger: section, start: 'top 78%', once: true }
      })
        .to(panel, { autoAlpha: 1, y: 0, duration: .72, ease: 'power3.out' }, 0)
        .to(title, { autoAlpha: 1, y: 0, clipPath: 'inset(0 0 0% 0)', duration: .62, ease: 'power3.out' }, .12)
        .to(intro, { autoAlpha: 1, y: 0, duration: .42 }, .38)
        .to(form, { autoAlpha: 1, y: 0, duration: .5 }, .52)
        .to(signature, { autoAlpha: 1, y: 0, duration: .48 }, .68);

      gsap.fromTo(image,
        { yPercent: compact ? 1.5 : 2.2, scale: 1.025 },
        {
          yPercent: compact ? -1.5 : -2.2,
          scale: 1,
          ease: 'none',
          scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: .8 }
        }
      );
    });
  };
  setupMoreBrioMotion();
  document.addEventListener('shopify:section:load', setupMoreBrioMotion);
  const setupContentMotion = () => {
    document.querySelectorAll('.brio-footer').forEach((section) => {
      if (section.dataset.contentMotionReady) return;
      section.dataset.contentMotionReady = 'true';
      section.classList.add('brio-content-motion-ready');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reducedMotion.matches || !('IntersectionObserver' in window)) {
        section.classList.add('is-content-visible');
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        section.classList.add('is-content-visible');
        observer.disconnect();
      }, { threshold: .04, rootMargin: '0px 0px -6% 0px' });
      observer.observe(section);
    });
  };
  setupContentMotion();
  document.addEventListener('shopify:section:load', setupContentMotion);
  let lastTrigger;
  const root = window.Shopify?.routes?.root || '/';
  const dialog = document.getElementById('brio-cart-confirm');
  const announce = (message) => { document.getElementById('brio-status').textContent = message; };
  const closeDialog = () => { dialog.close(); lastTrigger?.focus(); };
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-close-cart]')) closeDialog();
    const menuLink = event.target.closest('.brio-mobile-menu a');
    if (menuLink) {
      const target = new URL(menuLink.href, location.href);
      // Keep the old menu covering the page until a different Shopify page is ready.
      if (target.origin === location.origin && target.pathname === location.pathname && target.search === location.search)
        menuLink.closest('details').open = false;
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') document.querySelectorAll('.brio-mobile-menu[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); });
  });
  dialog?.addEventListener('click', (event) => { if (event.target === dialog) { const b = dialog.getBoundingClientRect(); if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) closeDialog(); } });
  dialog?.addEventListener('cancel', () => { lastTrigger?.focus(); });
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-brio-variant]');
    if (!select) return;
    const option = select.matches('input[type="radio"]') ? select : select.selectedOptions[0];
    const form = select.closest('form');
    form.querySelector('[data-brio-price]').textContent = option.dataset.price;
    form.querySelector('[data-brio-unit]').textContent = option.dataset.unit;
    form.querySelector('button[type="submit"]').disabled = option.dataset.available !== 'true';
    form.querySelector('[data-add-label]').textContent = option.dataset.available === 'true' ? 'Ajouter au panier' : 'Épuisé';
  });
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.brio-add-form');
    if (!form || !window.fetch || !dialog?.showModal) return;
    event.preventDefault();
    if (form.dataset.busy === 'true') return;
    form.dataset.busy = 'true';
    const button = form.querySelector('button[type="submit"]');
    const label = button.querySelector('[data-add-label]');
    const oldLabel = label.textContent;
    const error = form.querySelector('[data-form-error]');
    button.disabled = true; button.setAttribute('aria-busy', 'true'); label.textContent = 'Ajout en cours…'; error.hidden = true;
    let added = false;
    try {
      const data = new FormData(form);
      const response = await fetch(root + 'cart/add.js', { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Ce produit n’a pas pu être ajouté. Réessaie dans un instant.');
      added = true;
      const item = result.items?.[0] || result;
      const message = (item.product_title || item.title || 'Ton pack BRIO') + ' a été ajouté à ton panier.';
      dialog.querySelector('[data-cart-message]').textContent = message;
      lastTrigger = button;
      if (!dialog.open) dialog.showModal();
      announce(message);
      try {
        const cartResponse = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        if (cartResponse.ok) { const cart = await cartResponse.json(); document.querySelectorAll('[data-brio-cart-count]').forEach(el => { el.textContent = cart.item_count; }); }
      } catch (_) { /* The item is already in the cart; a count refresh must not report an add failure. */ }
    } catch (err) {
      error.textContent = err.message || 'Connexion interrompue. Consulte ton panier avant de réessayer.'; error.hidden = false; announce(error.textContent);
    } finally {
      form.dataset.busy = 'false'; button.disabled = false; button.removeAttribute('aria-busy'); label.textContent = oldLabel;
      if (!added) error.focus?.();
    }
  });
})();
